import { FlatAspect } from "@/models/HomePageSearchResponse";
import { Box, Divider, styled, Typography } from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import AspectMenu from "./AspectMenu";
import _ from "lodash";

export const LANDSCAPE_MAX_WIDTH = "md";

function SingleOption({
  option,
  selectedValues,
  onChange,
}: {
  option: FlatAspect;
  selectedValues: FlatAspect[];
  onChange: (selectedValues: FlatAspect[], keepOpen: boolean) => void;
}) {
  const isSelected = selectedValues.some((x) => x.id === option.id);
  const newSelectedValues = isSelected
    ? selectedValues.filter((x) => x.id !== option.id)
    : [...selectedValues, option];

  return (
    <Option
      isSelected={isSelected}
      option={option}
      onClick={(keepOpen) => onChange(newSelectedValues, keepOpen)}
    />
  );
}

function Option({
  isSelected,
  option,
  onClick,
}: {
  isSelected: boolean;
  option: { id: string; label: string; count?: number };
  onClick: (keepOpen: boolean) => void;
}) {
  return (
    <Box
      key={option.id}
      sx={{
        cursor: "pointer",
        "&:hover .option-label": {
          textDecoration: "underline",
        },
        display: "flex",
        gap: 1,
        paddingTop: 0.5,
        paddingBottom: 0.5,
        alignItems: "center",
      }}
    >
      <Box
        onClick={() => onClick(true)}
        sx={{
          display: "flex",
          alignItems: "center",
        }}
      >
        {isSelected ? <CheckBoxIcon /> : <CheckBoxOutlineBlankIcon />}
      </Box>
      <Typography className="option-label" onClick={() => onClick(false)}>
        {option.label}
      </Typography>
      <Typography variant="caption" sx={{ color: "grey.700" }}>
        ({option.count})
      </Typography>
    </Box>
  );
}

function OptionsList({
  options,
  selectedValues,
  onChange,
}: {
  options: FlatAspect[];
  selectedValues: FlatAspect[];
  onChange: (selectedValues: FlatAspect[], keepOpen: boolean) => void;
}) {
  const groups = _.uniqBy(
    options.map((x) => ({ id: x.groupId, label: x.groupLabel })),
    (x) => x.id
  );
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      {groups.map((group) => {
        return (
          <Box
            key={group.id}
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <Typography variant="h6">{group.label}</Typography>

            <MenuSectionBody>
              {options
                .filter((x) => x.groupId === group.id)
                .map((option) => {
                  return (
                    <SingleOption
                      key={option.id}
                      option={option}
                      selectedValues={selectedValues}
                      onChange={onChange}
                    />
                  );
                })}
            </MenuSectionBody>
          </Box>
        );
      })}
    </Box>
  );
}

const MenuSection = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
}));

const MenuSectionBody = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  flexWrap: "wrap",
  maxHeight: "500px",
  columnGap: 6,
  [theme.breakpoints.down(LANDSCAPE_MAX_WIDTH)]: {
    flexWrap: "nowrap",
  },
}));

export default function AspectMultiMenu({
  label,
  options,
  selectedValues,
  onChange,
  icon,
  isFilterBarExpanded,
  topSlot,
  hideOptions,
}: {
  label: string;
  options: FlatAspect[];
  selectedValues: FlatAspect[];
  onChange: (selectedValues: FlatAspect[], skipRefresh: boolean) => void;
  icon: React.ReactNode;
  isFilterBarExpanded: boolean;
  topSlot?: React.ReactNode;
  hideOptions?: boolean;
}) {
  const topOptions = options.filter((x) => x.isTop);
  const selectedLabel = selectedValues.map((x) => x.label).join(", ");

  return (
    <>
      {(options.length > 0 || topSlot) && (
        <AspectMenu
          label={label}
          icon={icon}
          selectedLabel={selectedLabel}
          isSelected={!!selectedLabel}
          isFilterBarExpanded={isFilterBarExpanded}
          onClear={() => onChange([], false)}
          body={(handleClose) => {
            const handleOnChange = (
              newSelectedValues: FlatAspect[],
              keepOpen: boolean
            ) => {
              onChange(newSelectedValues, keepOpen);
              if (!keepOpen) {
                handleClose();
              }
            };
            return (
              <Box>
                {topSlot}
                <Box
                  sx={(theme) => ({
                    padding: 4,
                    display: "flex",
                    gap: 4,
                    [theme.breakpoints.down(LANDSCAPE_MAX_WIDTH)]: {
                      flexDirection: "column",
                      padding: 2,
                      gap: 1,
                    },
                  })}
                >
                  {!hideOptions && (
                    <>
                      {topOptions.length > 0 && (
                        <>
                          <MenuSection>
                            <Typography variant="h6">Top {label}s</Typography>
                            <OptionsList
                              options={topOptions}
                              selectedValues={selectedValues}
                              onChange={handleOnChange}
                            />
                          </MenuSection>
                          <Divider orientation="vertical" flexItem />
                        </>
                      )}
                      <MenuSection>
                        {topOptions.length > 0 && (
                          <Typography variant="h6">All {label}s</Typography>
                        )}
                        <OptionsList
                          // AJB: better to avoid duplicating on the server?
                          options={options.filter((x) => !x.isTop)}
                          selectedValues={selectedValues}
                          onChange={handleOnChange}
                        />
                      </MenuSection>
                    </>
                  )}
                </Box>
              </Box>
            );
          }}
        />
      )}
    </>
  );
}
